// notifications.tsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  fetchOverview,
  fetchInactiveBuckets,
  fetchAgeBuckets,
  fetchPremiumStats,
  fetchEmailTemplates,
  fetchEmailTemplateMeta,
  updateEmailTemplate,
  sendNotification,
  sendRecommendations,
  sendEmailUsingTemplate,
  estimateTarget,
  publishLegal,
  type Overview,
  type InactiveBuckets,
  type AgeBuckets,
  type PremiumStats,
  type InactiveSegment,
  type EmailTemplateListItem,
  type EmailTemplateMeta,
  type TargetEstimate,
} from '../../../../api/notifications';
import { API_BASE, getAuthHeaders } from '../../../../api/config';
import '../../../../assets/styles/notifications.scss';

type AgeBucket = '0_6' | '7_12' | '13_17' | '18_plus';
type PremiumPlan = 'BASIC' | 'STANDARD' | 'FAMILY';
type PremiumStatus = 'active' | 'expired' | 'expiring_7d' | 'never';
type Channel = 'inapp' | 'email';
type LegalChange = 'TERMS' | 'PRIVACY' | 'BOTH';

const TEMPLATE_BY_SECTION = {
  // domyślny szablon NEWS/nowości
  general: 'important_info',
  activity: 'reengage_inactive',
  legal: 'legal_change_notice',
} as const;

const NEWSLETTER_TEMPLATE_TKEY = 'newsletter_general';

const TEMPLATE_BY_PREMIUM_STATUS: Record<PremiumStatus, string> = {
  expired: 'promo_expired',
  expiring_7d: 'promo_expiring7d',
  active: 'newsletter_general',
  never: 'promo_never',
};

type RenderedPreview = { subject: string; html: string; text?: string } | null;

async function previewTemplate(tkey: string): Promise<RenderedPreview> {
  if (!tkey) return null;
  const v = Date.now(); // cache-busting
  const res = await fetch(`${API_BASE}/admin/email-templates/${tkey}/preview?v=${v}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ vars: {} }),
    cache: 'no-store',
  });
  if (!res.ok) throw new Error(`Preview ${res.status}`);
  const data = await res.json();
  return data?.rendered || null;
}

/* ===================== KOMPONENT GŁÓWNY ===================== */
export default function Notifications() {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const [overview, setOverview] = useState<Overview | null>(null);
  const [inactive, setInactive] = useState<InactiveBuckets | null>(null);
  const [ages, setAges] = useState<AgeBuckets | null>(null);
  const [premium, setPremium] = useState<PremiumStats | null>(null);
  const [templates, setTemplates] = useState<EmailTemplateListItem[]>([]);
  const [premiumEstimate, setPremiumEstimate] = useState<TargetEstimate | null>(null);
  const [premiumEstimateLoading, setPremiumEstimateLoading] = useState(false);

  // Accordions
  const [openGeneral, setOpenGeneral] = useState(false);
  const [openActivity, setOpenActivity] = useState(false);
  const [openPremium, setOpenPremium] = useState(false);

  // ====== NOWOŚCI (dawniej OGÓLNE) ======
  const [gChannel, setGChannel] = useState<Channel>('inapp');
  const [gTitle, setGTitle] = useState('Hej od PolyLect 👋');
  const [gMsg, setGMsg] = useState('Mamy świeże treści. Wpadniesz dzisiaj?');
  const [gLongMsg, setGLongMsg] = useState('');
  const [gTemplateKey, setGTemplateKey] = useState<string>('');
  const [gTplMeta, setGTplMeta] = useState<EmailTemplateMeta | null>(null);
  const [gPreview, setGPreview] = useState<RenderedPreview>(null);
  const [gPreviewErr, setGPreviewErr] = useState<string | null>(null);
  const [gEditOpen, setGEditOpen] = useState(false);
  const [gEditSubject, setGEditSubject] = useState('');
  const [gEditHtml, setGEditHtml] = useState('');
  const [gEmailSubject, setGEmailSubject] = useState('');

  // ====== NEWSLETTER ======
  const [openNewsletter, setOpenNewsletter] = useState(false);
  const [nChannel, setNChannel] = useState<Channel>('email'); // newsletter głównie e-mail
  const [nTitle, setNTitle] = useState('Nowości z PolyLect 📬');
  const [nMsg, setNMsg] = useState('Sprawdź, co nowego przygotowaliśmy dla Ciebie.');
  const [nLongMsg, setNLongMsg] = useState('');
  const [nTemplateKey, setNTemplateKey] = useState<string>('');
  const [nTplMeta, setNTplMeta] = useState<EmailTemplateMeta | null>(null);
  const [nPreview, setNPreview] = useState<RenderedPreview | null>(null);
  const [nPreviewErr, setNPreviewErr] = useState<string | null>(null);
  const [nEditOpen, setNEditOpen] = useState(false);
  const [nEditSubject, setNEditSubject] = useState('');
  const [nEditHtml, setNEditHtml] = useState('');
  const [nEmailSubject, setNEmailSubject] = useState('');


  // ====== AKTYWNOŚĆ ======
  const [aChannel, setAChannel] = useState<Channel>('inapp');
  const [aTitle, setATitle] = useState('Wracamy?');
  const [aMsg, setAMsg] = useState('Długo Cię nie było—zobacz co nowego!');
  const [aLongMsg, setALongMsg] = useState('');
  const [aSegment, setASegment] = useState<InactiveSegment>('inactive_7_14');
  const [aTemplateKey, setATemplateKey] = useState<string>('');
  const [aTplMeta, setATplMeta] = useState<EmailTemplateMeta | null>(null);
  const [aPreview, setAPreview] = useState<RenderedPreview>(null);
  const [aPreviewErr, setAPreviewErr] = useState<string | null>(null);
  const [aEditOpen, setAEditOpen] = useState(false);
  const [aEditSubject, setAEditSubject] = useState('');
  const [aEditHtml, setAEditHtml] = useState('');

  // ====== POLECANE ======
  const [rLimit, setRLimit] = useState(5);
  const [rAge, setRAge] = useState<AgeBucket>('18_plus');

  // ====== PREMIUM ======
  const [pChannel, setPChannel] = useState<Channel>('inapp');
  const [pTitle, setPTitle] = useState('Twoje Premium');
  const [pMsg, setPMsg] = useState('Brakuje Ci dostępu? Wróć na Premium i korzystaj bez ograniczeń.');
  const [pLongMsg, setPLongMsg] = useState('');
  const [pStatus, setPStatus] = useState<PremiumStatus>('expired');
  const [pPlans, setPPlans] = useState<Record<PremiumPlan, boolean>>({ BASIC: true, STANDARD: true, FAMILY: true });
  const [pTemplateKey, setPTemplateKey] = useState<string>('');
  const [pTplMeta, setPTplMeta] = useState<EmailTemplateMeta | null>(null);
  const [pPreview, setPPreview] = useState<RenderedPreview>(null);
  const [pPreviewErr, setPPreviewErr] = useState<string | null>(null);
  const [pEditOpen, setPEditOpen] = useState(false);
  const [pEditSubject, setPEditSubject] = useState('');
  const [pEditHtml, setPEditHtml] = useState('');

  // ====== LEGAL (TERMS/PRIVACY) ======
  const [lChannel, setLChannel] = useState<Channel>('inapp');
  const [lOpen, setLOpen] = useState(false);
  const [lChange, setLChange] = useState<LegalChange>('BOTH');
  const [lVersion, setLVersion] = useState<string>(new Date().toISOString().slice(0, 10));
  const [lBusy, setLBusy] = useState(false);
  const [lTitle, setLTitle] = useState('Zmiana regulaminu');
  const [lMsg, setLMsg] = useState(
    'Zaktualizowaliśmy Regulamin i/lub Politykę prywatności. Akceptacja będzie wymagana przy kolejnym logowaniu.'
  );
  const [lTemplateKey, setLTemplateKey] = useState<string>('');
  const [lTplMeta, setLTplMeta] = useState<EmailTemplateMeta | null>(null);
  const [lPreview, setLPreview] = useState<RenderedPreview>(null);
  const [lPreviewErr, setLPreviewErr] = useState<string | null>(null);
  const [lEditOpen, setLEditOpen] = useState(false);
  const [lEditSubject, setLEditSubject] = useState('');
  const [lEditHtml, setLEditHtml] = useState('');

  // ====== POJEDYNCZY UŻYTKOWNIK ======
  const [openSingleUser, setOpenSingleUser] = useState(false);

  const [uChannel, setUChannel] = useState<Channel>('inapp');
  const [uUserId, setUUserId] = useState<string>('');
  const [uTitle, setUTitle] = useState('Wiadomość od PolyLect 👋');
  const [uMsg, setUMsg] = useState('Cześć! Mamy coś specjalnie dla Ciebie.');
  const [uLongMsg, setULongMsg] = useState('');

  const [uTemplateKey, setUTemplateKey] = useState<string>('');
  const [uTplMeta, setUTplMeta] = useState<EmailTemplateMeta | null>(null);
  const [uPreview, setUPreview] = useState<RenderedPreview>(null);
  const [uPreviewErr, setUPreviewErr] = useState<string | null>(null);
  const [uEditOpen, setUEditOpen] = useState(false);
  const [uEditSubject, setUEditSubject] = useState('');
  const [uEditHtml, setUEditHtml] = useState('');
  // NOWE: temat maila per wysyłka (nadpisuje szablon)
  const [uEmailSubject, setUEmailSubject] = useState('');

  const pSelectedPlans = useMemo(
    () => Object.entries(pPlans).filter(([, v]) => v).map(([k]) => k as PremiumPlan),
    [pPlans]
  );

  useEffect(() => {
    let cancelled = false;

    async function run() {
      try {
        setPremiumEstimateLoading(true);

        // premiumFilter używany tylko do estymacji:
        const premiumFilter =
          pStatus === 'never'
            ? { status: 'never' as const }
            : {
              status: pStatus,
              plans: pSelectedPlans.length ? pSelectedPlans : undefined,
            };

        const est = await estimateTarget({ premium: premiumFilter });
        if (!cancelled) {
          setPremiumEstimate(est);
        }
      } catch (e) {
        console.error('premium estimate error', e);
        if (!cancelled) {
          setPremiumEstimate(null);
        }
      } finally {
        if (!cancelled) {
          setPremiumEstimateLoading(false);
        }
      }
    }

    // nie ma sensu wywoływać, gdy jeszcze nie ma statusu (teoretycznie zawsze jest)
    run();

    return () => {
      cancelled = true;
    };
  }, [pStatus, pSelectedPlans]);

  function labelForChange(ch: LegalChange) {
    return ch === 'TERMS'
      ? 'Regulamin'
      : ch === 'PRIVACY'
        ? 'Polityka prywatności'
        : 'Regulamin + Polityka';
  }

  async function doPublish() {
    setErr(null);
    setLBusy(true);
    try {
      const r = await publishLegal({ change: lChange, version: lVersion });
      alert(`Opublikowano: ${labelForChange(lChange)} (wersja: ${lVersion})`);
    } catch (e: any) {
      setErr(e?.message || 'Błąd publikacji');
    } finally {
      setLBusy(false);
    }
  }

  async function publishAndNotifyLegal() {
    setErr(null);
    setLBusy(true);
    try {
      if (lChannel === 'email' && !lTemplateKey) {
        throw new Error('Wybierz szablon e-mail.');
      }

      const pub = await publishLegal({ change: lChange, version: lVersion });
      const publishedVersion = pub?.terms?.version || pub?.privacy?.version || lVersion;

      if (lChannel === 'inapp') {
        const t = (lTitle || defaultLegalTitle(lChange)).trim();
        const m = (lMsg || defaultLegalMessage(lChange)).trim();
        const r = await sendNotification({
          title: t,
          message: m,
          segment: 'all',
          sendLocal: true,
          sendEmail: false,
          sendPush: true,
          forceLocalAll: true,
        });
        alert(`Opublikowano ${publishedVersion} i wysłano in-app: ${r.insertedLocal}`);
      } else {
        const vars = {
          appName: 'PolyLect',
          docTypeLabel: labelForChange(lChange),
          version: publishedVersion,
          legalUrl: `${window.location.origin}/legal`,
          supportUrl: `${window.location.origin}/support`,
          year: new Date().getFullYear(),
        };
        const r = await sendEmailUsingTemplate({
          templateKey: lTemplateKey,
          vars,
          segment: 'all',
          forceAllEmails: true,
        } as any);
        alert(
          `Opublikowano ${publishedVersion} i wysłano e-mail: ${r.sentEmail}${r.emailErrors.length ? ` (błędy: ${r.emailErrors.length})` : ''
          }`
        );
      }
    } catch (e: any) {
      setErr(e?.message || 'Błąd publikacji/wysyłki');
    } finally {
      setLBusy(false);
    }
  }

  // ====== INIT ======
  useEffect(() => {
    void load();
  }, []);

  async function load() {
    setLoading(true);
    setErr(null);
    try {
      const [ov, inact, ag, pr, tmpls] = await Promise.all([
        fetchOverview(),
        fetchInactiveBuckets(),
        fetchAgeBuckets(),
        fetchPremiumStats(),
        fetchEmailTemplates(),
      ]);
      setOverview(ov);
      setInactive(inact);
      setAges(ag);
      setPremium(pr);

      const active = (tmpls || []).filter((t) => t.is_active);
      setTemplates(active);

      const findKey = (k: string) => active.find((t) => t.tkey === k)?.tkey || '';

      const gk = findKey(TEMPLATE_BY_SECTION.general);
      const ak = findKey(TEMPLATE_BY_SECTION.activity);
      const pk = findKey(TEMPLATE_BY_PREMIUM_STATUS[pStatus]);
      const lk = findKey(TEMPLATE_BY_SECTION.legal);
      const nk = findKey(NEWSLETTER_TEMPLATE_TKEY);

      setGTemplateKey(gk || active[0]?.tkey || '');
      setATemplateKey(ak);
      setPTemplateKey(pk);
      setLTemplateKey(lk);
      setNTemplateKey(nk || active[0]?.tkey || '');

      if (gk || active[0]) await loadMeta('g', gk || active[0]!.tkey);
      if (ak) await loadMeta('a', ak);
      if (pk) await loadMeta('p', pk);
      if (lk) await loadMeta('l', lk);
      if (nk || active[0]) await loadMeta('n', nk || active[0]!.tkey);
    } catch (e: any) {
      setErr(e?.message || 'Błąd ładowania');
    } finally {
      setLoading(false);
    }
  }

  // zmiana statusu premium -> szablon
  useEffect(() => {
    if (!templates.length) return;
    const tkey = TEMPLATE_BY_PREMIUM_STATUS[pStatus];
    const exists = templates.find((t) => t.tkey === tkey);
    const next = exists ? tkey : '';
    setPTemplateKey(next);
    if (next) void loadMeta('p', next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pStatus, templates]);

  // AUTO: po przełączeniu kanału "Nowości" na e-mail ustaw domyślny szablon (jeśli brak)
  useEffect(() => {
    if (gChannel === 'email' && !gTemplateKey && templates.length) {
      const def =
        templates.find((t) => t.tkey === TEMPLATE_BY_SECTION.general) ?? templates[0];
      if (def) {
        setGTemplateKey(def.tkey);
        void loadMeta('g', def.tkey);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gChannel, templates]);

  // AUTO: po przełączeniu kanału Legal na e-mail ustaw domyślny szablon (jeśli brak)
  useEffect(() => {
    if (lChannel === 'email' && !lTemplateKey && templates.length) {
      const def =
        templates.find((t) => t.tkey === TEMPLATE_BY_SECTION.legal) ?? templates[0];
      if (def) {
        setLTemplateKey(def.tkey);
        void loadMeta('l', def.tkey);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lChannel, templates]);

  // ====== META & PREVIEW ======
  async function loadMeta(scope: 'g' | 'a' | 'p' | 'l' | 'u' | 'n', tkey: string) {
    try {
      const meta = await fetchEmailTemplateMeta(tkey);
      if (scope === 'g') {
        setGTplMeta(meta);
        setGEditSubject(meta.subject || '');
        setGEditHtml(meta.html || '');
        // domyślnie wypełnij pole "Temat e-mail" tematem z szablonu
        setGEmailSubject(meta.subject || '');
      }
      if (scope === 'a') {
        setATplMeta(meta);
        setAEditSubject(meta.subject || '');
        setAEditHtml(meta.html || '');
      }
      if (scope === 'p') {
        setPTplMeta(meta);
        setPEditSubject(meta.subject || '');
        setPEditHtml(meta.html || '');
      }
      if (scope === 'l') {
        setLTplMeta(meta);
        setLEditSubject(meta.subject || '');
        setLEditHtml(meta.html || '');
      }
      if (scope === 'u') {
        setUTplMeta(meta);
        setUEditSubject(meta.subject || '');
        setUEditHtml(meta.html || '');
        // domyślnie wypełnij temat dla pojedynczego użytkownika
        setUEmailSubject(meta.subject || '');
      }
      if (scope === 'n') {
        setNTplMeta(meta);
        setNEditSubject(meta.subject || '');
        setNEditHtml(meta.html || '');
        setNEmailSubject(meta.subject || '');
      }
    } catch (e: any) {
      console.error('template meta error', e?.message);
      if (scope === 'g') setGTplMeta(null);
      if (scope === 'a') setATplMeta(null);
      if (scope === 'p') setPTplMeta(null);
      if (scope === 'l') setLTplMeta(null);
      if (scope === 'u') setUTplMeta(null);
      if (scope === 'n') setNTplMeta(null);
    }
  }

  // podglądy (z serwera)
  useEffect(() => {
    let stop = false;
    (async () => {
      if (!openGeneral || !gTemplateKey) {
        setGPreview(null);
        setGPreviewErr(null);
        return;
      }
      try {
        const prev = await previewTemplate(gTemplateKey);
        if (!stop) {
          setGPreview(prev);
          setGPreviewErr(null);
        }
      } catch (e: any) {
        if (!stop) {
          setGPreview(null);
          setGPreviewErr(e?.message || 'Błąd podglądu');
        }
      }
    })();
    return () => {
      stop = true;
    };
  }, [openGeneral, gTemplateKey]);

  useEffect(() => {
    let stop = false;
    (async () => {
      if (!openActivity || !aTemplateKey) {
        setAPreview(null);
        setAPreviewErr(null);
        return;
      }
      try {
        const prev = await previewTemplate(aTemplateKey);
        if (!stop) {
          setAPreview(prev);
          setAPreviewErr(null);
        }
      } catch (e: any) {
        if (!stop) {
          setAPreview(null);
          setAPreviewErr(e?.message || 'Błąd podglądu');
        }
      }
    })();
    return () => {
      stop = true;
    };
  }, [openActivity, aTemplateKey]);

  useEffect(() => {
    let stop = false;
    (async () => {
      if (!openPremium || !pTemplateKey) {
        setPPreview(null);
        setPPreviewErr(null);
        return;
      }
      try {
        const prev = await previewTemplate(pTemplateKey);
        if (!stop) {
          setPPreview(prev);
          setPPreviewErr(null);
        }
      } catch (e: any) {
        if (!stop) {
          setPPreview(null);
          setPPreviewErr(e?.message || 'Błąd podglądu');
        }
      }
    })();
    return () => {
      stop = true;
    };
  }, [openPremium, pTemplateKey]);

  useEffect(() => {
    let stop = false;
    (async () => {
      if (!lOpen || !lTemplateKey) {
        setLPreview(null);
        setLPreviewErr(null);
        return;
      }
      try {
        const prev = await previewTemplate(lTemplateKey);
        if (!stop) {
          setLPreview(prev);
          setLPreviewErr(null);
        }
      } catch (e: any) {
        if (!stop) {
          setLPreview(null);
          setLPreviewErr(e?.message || 'Błąd podglądu');
        }
      }
    })();
    return () => {
      stop = true;
    };
  }, [lOpen, lTemplateKey]);

  useEffect(() => {
    let stop = false;
    (async () => {
      if (!openSingleUser || !uTemplateKey) {
        setUPreview(null);
        setUPreviewErr(null);
        return;
      }
      try {
        const prev = await previewTemplate(uTemplateKey);
        if (!stop) {
          setUPreview(prev);
          setUPreviewErr(null);
        }
      } catch (e: any) {
        if (!stop) {
          setUPreview(null);
          setUPreviewErr(e?.message || 'Błąd podglądu');
        }
      }
    })();
    return () => {
      stop = true;
    };
  }, [openSingleUser, uTemplateKey]);

  useEffect(() => {
    let stop = false;
    (async () => {
      if (!openNewsletter || !nTemplateKey) {
        setNPreview(null);
        setNPreviewErr(null);
        return;
      }
      try {
        const prev = await previewTemplate(nTemplateKey);
        if (!stop) {
          setNPreview(prev);
          setNPreviewErr(null);
        }
      } catch (e: any) {
        if (!stop) {
          setNPreview(null);
          setNPreviewErr(e?.message || 'Błąd podglądu');
        }
      }
    })();
    return () => {
      stop = true;
    };
  }, [openNewsletter, nTemplateKey]);


  const tplName = (key: string) => templates.find((t) => t.tkey === key)?.name || (key || '—');

  // ====== ACTIONS ======
  async function sendGeneral() {
    setErr(null);
    if (gChannel === 'inapp') {
      if (!gTitle.trim()) {
        setErr('Uzupełnij tytuł.');
        return;
      }
      const r = await sendNotification({
        title: gTitle,
        message: gMsg || '',
        longMessage: gLongMsg || undefined,
        segment: 'all',
        sendLocal: true,
        sendEmail: false,
        sendPush: true,
        forceLocalAll: true,
      });
      alert(`Wysłano — in-app: ${r.insertedLocal}`);
    } else {
      if (!gTemplateKey) {
        setErr('Wybierz szablon e-mail.');
        return;
      }
      const payload: any = {
        templateKey: gTemplateKey,
        segment: 'all',
        forceAllEmails: true,
      };
      const trimmed = gEmailSubject.trim();
      if (trimmed) {
        // to pole powinno być obsłużone przez backend
        payload.subjectOverride = trimmed;
      }
      const r = await sendEmailUsingTemplate(payload);
      alert(
        `Wysłano — e-mail: ${r.sentEmail}${r.emailErrors.length ? ` (błędy: ${r.emailErrors.length})` : ''
        }`
      );
    }
  }

  async function sendNewsletter() {
    setErr(null);

    if (nChannel === 'inapp') {
      if (!nTitle.trim() || !nMsg.trim()) {
        setErr('Uzupełnij tytuł i wiadomość.');
        return;
      }

      const r = await sendNotification({
        title: nTitle,
        message: nMsg,
        longMessage: nLongMsg || undefined,
        segment: 'all',
        sendLocal: true,
        sendEmail: false,
        sendPush: true,
        // UWAGA: tu NIE ustawiamy forceLocalAll
        // → backend wyśle tylko do users.accepts_local_notifications = TRUE
        // co w praktyce jest „zgoda na powiadomienia lokalne”
      });

      alert(`Wysłano newsletter in-app do: ${r.insertedLocal} użytkowników`);
    } else {
      if (!nTemplateKey) {
        setErr('Wybierz szablon e-mail dla newslettera.');
        return;
      }

      const payload: any = {
        templateKey: nTemplateKey,
        segment: 'all',
        forceAllEmails: false,
      };

      const trimmed = nEmailSubject.trim();
      if (trimmed) {
        payload.subjectOverride = trimmed;
      }

      const r = await sendEmailUsingTemplate(payload);

      alert(
        `Wysłano newsletter e-mail: ${r.sentEmail}${r.emailErrors.length ? ` (błędy: ${r.emailErrors.length})` : ''
        }`
      );
    }
  }


  async function sendActivity() {
    setErr(null);
    if (aChannel === 'inapp') {
      if (!aTitle.trim() || !aMsg.trim()) {
        setErr('Uzupełnij tytuł i wiadomość.');
        return;
      }

      const r = await sendNotification({
        title: aTitle,
        message: aMsg,
        longMessage: aLongMsg || undefined,
        segment: aSegment,
        sendLocal: true,
        sendEmail: false,
        sendPush: true,
        forceLocalAll: true,
        pushData: {
          type: 'cms_notification',
          context: 'activity',
          screen: 'NotificationsScreen',
        },
      });

      alert(`Wysłano — in-app: ${r.insertedLocal}`);
    } else {
      if (!aTemplateKey) {
        setErr('Wybierz szablon e-mail.');
        return;
      }
      const r = await sendEmailUsingTemplate({
        templateKey: aTemplateKey,
        segment: aSegment,
      } as any);
      alert(
        `Wysłano — e-mail: ${r.sentEmail}${r.emailErrors.length ? ` (błędy: ${r.emailErrors.length})` : ''
        }`
      );
    }
  }

  async function sendRecommended() {
    setErr(null);
    const res = await sendRecommendations({ limit: rLimit, ageBucket: rAge });
    alert(`Wysłano rekomendacje do ${res.results.length} użytkowników (in-app).`);
  }

  async function sendPremium() {
    setErr(null);
    const premiumFilter = { status: pStatus, plans: pSelectedPlans };
    if (pChannel === 'inapp') {
      if (!pTitle.trim() || !pMsg.trim()) {
        setErr('Uzupełnij tytuł i wiadomość.');
        return;
      }
      if (!pSelectedPlans.length) {
        setErr('Zaznacz przynajmniej jeden plan.');
        return;
      }

      if (pStatus !== 'never' && !pSelectedPlans.length) {
        setErr('Zaznacz przynajmniej jeden plan.');
        return;
      }

      const r = await sendNotification({
        title: pTitle,
        message: pMsg,
        longMessage: pLongMsg || undefined,
        premium: premiumFilter,
        sendLocal: true,
        sendEmail: false,
        sendPush: true,
        pushData: {
          type: 'cms_notification',
          context: 'premium',
          screen: 'SettingsPage',
        },
      });

      alert(`Wysłano — in-app: ${r.insertedLocal}`);
    } else {
      if (!pTemplateKey) {
        setErr('Wybierz szablon e-mail.');
        return;
      }
      const r = await sendEmailUsingTemplate({
        templateKey: pTemplateKey,
        premium: premiumFilter,
      });
      alert(
        `Wysłano — e-mail: ${r.sentEmail}${r.emailErrors.length ? ` (błędy: ${r.emailErrors.length})` : ''
        }`
      );
    }
  }

  async function sendSingleUser() {
    setErr(null);

    const id = Number(uUserId);
    if (!id || Number.isNaN(id)) {
      setErr('Podaj poprawne ID użytkownika (liczba).');
      return;
    }

    if (uChannel === 'inapp') {
      if (!uTitle.trim() || !uMsg.trim()) {
        setErr('Uzupełnij tytuł i wiadomość.');
        return;
      }

      const r = await sendNotification({
        title: uTitle,
        message: uMsg,
        longMessage: uLongMsg || undefined,
        userIds: [id],
        sendLocal: true,
        sendEmail: false,
        sendPush: true,
      });

      alert(
        `Wysłano do użytkownika #${id} — in-app: ${r.insertedLocal}, push: ${r.sentPush ?? 0
        }${r.pushErrors && r.pushErrors.length ? ` (błędy push: ${r.pushErrors.length})` : ''
        }`
      );
    } else {
      if (!uTemplateKey) {
        setErr('Wybierz szablon e-mail.');
        return;
      }

      const payload: any = {
        templateKey: uTemplateKey,
        userIds: [id],
        forceAllEmails: true, // zawsze force dla pojedynczego usera
      };
      const trimmed = uEmailSubject.trim();
      if (trimmed) {
        payload.subjectOverride = trimmed;
      }

      const r = await sendEmailUsingTemplate(payload);

      alert(
        `Wysłano e-mail do użytkownika #${id}: ${r.sentEmail}${r.emailErrors.length ? ` (błędy: ${r.emailErrors.length})` : ''
        }`
      );
    }
  }

  // ====== COUNTS ======
  const generalCounts = useMemo(
    () => ({
      all: overview?.total_users ?? 0,
      local: overview?.eligible_local ?? 0,
      email: overview?.eligible_email ?? 0,
    }),
    [overview]
  );

  const activityCount = useMemo(() => {
    if (!inactive) return 0;
    switch (aSegment) {
      case 'inactive_7_14':
        return inactive.inactive_7_14 || 0;
      case 'inactive_14_30':
        return inactive.inactive_14_30 || 0;
      case 'inactive_30_60':
        return inactive.inactive_30_60 || 0;
      case 'inactive_gt_60':
        return inactive.inactive_gt_60 || 0;
      case 'all':
        return overview?.total_users || 0;
    }
  }, [inactive, aSegment, overview]);

  const ageCount = useMemo(() => {
    if (!ages) return 0;
    return rAge === '0_6'
      ? ages.age_0_6
      : rAge === '7_12'
        ? ages.age_7_12
        : rAge === '13_17'
          ? ages.age_13_17
          : ages.age_18_plus;
  }, [ages, rAge]);

  const premiumHint = useMemo(() => {
    if (!premium) return '—';
    console.log(pStatus)
    if (pStatus === 'never') {
      return 'Użytkownicy, którzy nigdy nie mieli Premium (liczba nie jest pokazywana tutaj).';
    }
    if (pStatus === 'expired')
      return `Po terminie: ${premium.expired_total} (B:${premium.expired_basic}, S:${premium.expired_standard}, F:${premium.expired_family})`;
    if (pStatus === 'expiring_7d') return `Kończą się ≤7 dni: ${premium.expiring_7d_total}`;
    return `Aktywne: ${premium.active_total} (B:${premium.active_basic}, S:${premium.active_standard}, F:${premium.active_family})`;
  }, [premium, pStatus]);

  // ====== RENDER ======
  return (
    <div className="notif-root">
      <header className="notif-header">
        <div>
          <h1>Powiadomienia</h1>
          <p className="muted">
            Kanał wybierasz: In-app (tytuł + treść) albo E-mail (sam szablon).
            Krótka treść idzie do push + listy, dłuższa jest widoczna dopiero w centrum powiadomień.
          </p>
        </div>
        <button className="btn" onClick={load} disabled={loading}>
          {loading ? 'Odświeżanie…' : 'Odśwież'}
        </button>
      </header>

      {err && (
        <div className="alert alert-error" style={{ marginBottom: 16 }}>
          {err}
        </div>
      )}

      <div className="accordion">
        {/* NOWOŚCI (dawniej OGÓLNE) */}
        <AccordionItem
          title="Wazne informacje"
          open={openGeneral}
          danger
          onOpenChange={(o) => {
            setOpenGeneral(o);
            if (o && gTemplateKey) void loadMeta('g', gTemplateKey);
          }}
        >
          <SectionStats
            rows={[
              { label: 'Wszyscy', value: generalCounts.all },
              { label: 'Eligible in-app', value: generalCounts.local },
              { label: 'Eligible e-mail', value: generalCounts.email },
            ]}
          />

          <ChannelSelect value={gChannel} onChange={setGChannel} />

          {gChannel === 'inapp' && (
            <>
              <InputRow label="Tytuł">
                <input value={gTitle} onChange={(e) => setGTitle(e.target.value)} />
              </InputRow>
              <InputRow label="Treść krótka (push + lista)">
                <textarea
                  rows={3}
                  value={gMsg}
                  onChange={(e) => setGMsg(e.target.value)}
                  placeholder="Krótka treść – będzie widoczna w pushu i na liście powiadomień"
                />
              </InputRow>
              <InputRow label="Treść szczegółowa (tylko centrum powiadomień, opcjonalna)">
                <textarea
                  rows={5}
                  value={gLongMsg}
                  onChange={(e) => setGLongMsg(e.target.value)}
                  placeholder="Dłuższa wiadomość, która pojawi się dopiero po wejściu w aplikacji w centrum powiadomień"
                />
              </InputRow>
            </>
          )}

          {gChannel === 'email' && (
            <>
              <InputRow label="Szablon e-mail (wybierz)">
                <select
                  value={gTemplateKey}
                  onChange={async (e) => {
                    const key = e.target.value;
                    setGTemplateKey(key);
                    if (key) {
                      await loadMeta('g', key);
                      try {
                        setGPreview(await previewTemplate(key));
                        setGPreviewErr(null);
                      } catch (err: any) {
                        setGPreview(null);
                        setGPreviewErr(err?.message || 'Błąd podglądu');
                      }
                    } else {
                      setGTplMeta(null);
                      setGPreview(null);
                      setGPreviewErr(null);
                      setGEmailSubject('');
                    }
                  }}
                >
                  <option value="">— wybierz —</option>
                  {templates.map((t) => (
                    <option key={t.tkey} value={t.tkey}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </InputRow>

              <InputRow label="Temat e-mail (opcjonalny, nadpisze temat z szablonu)">
                <input
                  value={gEmailSubject}
                  onChange={(e) => setGEmailSubject(e.target.value)}
                  placeholder={gTplMeta?.subject || 'Temat z szablonu'}
                  disabled={!gTemplateKey}
                />
              </InputRow>

              <TemplateRow
                label="Bieżący szablon"
                templateKey={gTemplateKey}
                templateName={tplName(gTemplateKey)}
                canEdit={!!gTplMeta}
                editOpen={gEditOpen}
                onToggleEdit={() => setGEditOpen((v) => !v)}
              />
              {gTplMeta && gEditOpen && (
                <TemplateTextareaEditor
                  subject={gEditSubject}
                  html={gEditHtml}
                  onChangeSubject={setGEditSubject}
                  onChangeHtml={setGEditHtml}
                  onSave={async () => {
                    await updateEmailTemplate(gTemplateKey, { subject: gEditSubject, html: gEditHtml });
                    await loadMeta('g', gTemplateKey);
                    try {
                      setGPreview(await previewTemplate(gTemplateKey));
                      setGPreviewErr(null);
                    } catch (e: any) {
                      setGPreview(null);
                      setGPreviewErr(e?.message || 'Błąd podglądu');
                    }
                    setGEditOpen(false);
                  }}
                />
              )}
              {gPreviewErr && <div className="muted error">{gPreviewErr}</div>}
              <TemplatePreview preview={gPreview} />
            </>
          )}

          <ActionsRow onClick={sendGeneral} loading={loading} label="Wyślij do wszystkich" primary />
        </AccordionItem>

        <AccordionItem
          title="Newsletter"
          open={openNewsletter}
          onOpenChange={(o) => {
            setOpenNewsletter(o);
            if (o && nTemplateKey) void loadMeta('n', nTemplateKey);
          }}
        >
          <SectionStats
            rows={[
              { label: 'Wszyscy', value: generalCounts.all },
              { label: 'Eligible e-mail (zgoda marketingowa)', value: generalCounts.email },
            ]}
          />

          <ChannelSelect value={nChannel} onChange={setNChannel} />

          {nChannel === 'inapp' && (
            <>
              <InputRow label="Tytuł">
                <input value={nTitle} onChange={(e) => setNTitle(e.target.value)} />
              </InputRow>
              <InputRow label="Treść krótka (push + lista)">
                <textarea
                  rows={3}
                  value={nMsg}
                  onChange={(e) => setNMsg(e.target.value)}
                />
              </InputRow>
              <InputRow label="Treść szczegółowa (opcjonalna)">
                <textarea
                  rows={5}
                  value={nLongMsg}
                  onChange={(e) => setNLongMsg(e.target.value)}
                />
              </InputRow>
            </>
          )}

          {nChannel === 'email' && (
            <>
              <InputRow label="Szablon e-mail (newsletter)">
                <select
                  value={nTemplateKey}
                  onChange={async (e) => {
                    const key = e.target.value;
                    setNTemplateKey(key);
                    if (key) {
                      await loadMeta('n', key);
                      try {
                        setNPreview(await previewTemplate(key));
                        setNPreviewErr(null);
                      } catch (err: any) {
                        setNPreview(null);
                        setNPreviewErr(err?.message || 'Błąd podglądu');
                      }
                    } else {
                      setNTplMeta(null);
                      setNPreview(null);
                      setNPreviewErr(null);
                      setNEmailSubject('');
                    }
                  }}
                >
                  <option value="">— wybierz —</option>
                  {templates.map((t) => (
                    <option key={t.tkey} value={t.tkey}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </InputRow>

              <InputRow label="Temat e-mail (opcjonalny)">
                <input
                  value={nEmailSubject}
                  onChange={(e) => setNEmailSubject(e.target.value)}
                  placeholder={nTplMeta?.subject || 'Temat z szablonu'}
                  disabled={!nTemplateKey}
                />
              </InputRow>

              <TemplateRow
                label="Bieżący szablon"
                templateKey={nTemplateKey}
                templateName={tplName(nTemplateKey)}
                canEdit={!!nTplMeta}
                editOpen={nEditOpen}
                onToggleEdit={() => setNEditOpen((v) => !v)}
              />
              {nTplMeta && nEditOpen && (
                <TemplateTextareaEditor
                  subject={nEditSubject}
                  html={nEditHtml}
                  onChangeSubject={setNEditSubject}
                  onChangeHtml={setNEditHtml}
                  onSave={async () => {
                    await updateEmailTemplate(nTemplateKey, {
                      subject: nEditSubject,
                      html: nEditHtml,
                    });
                    await loadMeta('n', nTemplateKey);
                    try {
                      setNPreview(await previewTemplate(nTemplateKey));
                      setNPreviewErr(null);
                    } catch (e: any) {
                      setNPreview(null);
                      setNPreviewErr(e?.message || 'Błąd podglądu');
                    }
                    setNEditOpen(false);
                  }}
                />
              )}
              {nPreviewErr && <div className="muted error">{nPreviewErr}</div>}
              <TemplatePreview preview={nPreview} />
            </>
          )}

          <ActionsRow onClick={sendNewsletter} loading={loading} label="Wyślij newsletter" primary />
        </AccordionItem>


        {/* AKTYWNOŚĆ */}
        <AccordionItem
          title="Aktywność"
          open={openActivity}
          onOpenChange={(o) => {
            setOpenActivity(o);
            if (o && aTemplateKey) void loadMeta('a', aTemplateKey);
          }}
        >
          <SectionStats
            rows={[
              { label: '7–14', value: inactive?.inactive_7_14 ?? '—' },
              { label: '14–30', value: inactive?.inactive_14_30 ?? '—' },
              { label: '30–60', value: inactive?.inactive_30_60 ?? '—' },
              { label: '>60', value: inactive?.inactive_gt_60 ?? '—' },
            ]}
          />

          <InputRow label="Grupa">
            <select value={aSegment} onChange={(e) => setASegment(e.target.value as InactiveSegment)}>
              <option value="inactive_7_14">Nieaktywni 7–14 dni</option>
              <option value="inactive_14_30">Nieaktywni 14–30 dni</option>
              <option value="inactive_30_60">Nieaktywni 30–60 dni</option>
              <option value="inactive_gt_60">Nieaktywni &gt; 60 dni</option>
              <option value="all">Wszyscy</option>
            </select>
            <div className="muted" style={{ marginLeft: 12 }}>
              Cel: {activityCount ?? 0}
            </div>
          </InputRow>

          <ChannelSelect value={aChannel} onChange={setAChannel} />

          <InputRow label="Tytuł">
            <input value={aTitle} onChange={(e) => setATitle(e.target.value)} />
          </InputRow>

          {aChannel === 'inapp' && (
            <>
              <InputRow label="Treść krótka (push + lista)">
                <textarea rows={3} value={aMsg} onChange={(e) => setAMsg(e.target.value)} />
              </InputRow>
              <InputRow label="Treść szczegółowa (centrum powiadomień, opcjonalna)">
                <textarea rows={5} value={aLongMsg} onChange={(e) => setALongMsg(e.target.value)} />
              </InputRow>
            </>
          )}

          {aChannel === 'email' && (
            <>
              <TemplateRow
                label="Szablon e-mail"
                templateKey={aTemplateKey}
                templateName={tplName(aTemplateKey)}
                canEdit={!!aTplMeta}
                editOpen={aEditOpen}
                onToggleEdit={() => setAEditOpen((v) => !v)}
              />
              {aTplMeta && aEditOpen && (
                <TemplateTextareaEditor
                  subject={aEditSubject}
                  html={aEditHtml}
                  onChangeSubject={setAEditSubject}
                  onChangeHtml={setAEditHtml}
                  onSave={async () => {
                    await updateEmailTemplate(aTemplateKey, { subject: aEditSubject, html: aEditHtml });
                    await loadMeta('a', aTemplateKey);
                    try {
                      setAPreview(await previewTemplate(aTemplateKey));
                      setAPreviewErr(null);
                    } catch (e: any) {
                      setAPreview(null);
                      setAPreviewErr(e?.message || 'Błąd podglądu');
                    }
                    setAEditOpen(false);
                  }}
                />
              )}
              {aPreviewErr && <div className="muted error">{aPreviewErr}</div>}
              <TemplatePreview preview={aPreview} />
            </>
          )}

          <ActionsRow onClick={sendActivity} loading={loading} label="Wyślij" primary />
        </AccordionItem>

        {/* PREMIUM */}
        <AccordionItem
          title="Premium"
          open={openPremium}
          onOpenChange={(o) => {
            setOpenPremium(o);
            if (o && pTemplateKey) void loadMeta('p', pTemplateKey);
          }}
        >
          <SectionStats
            rows={[
              { label: 'Aktywne', value: premium?.active_total ?? '—' },
              { label: 'Kończą się ≤7d', value: premium?.expiring_7d_total ?? '—' },
              { label: 'Po terminie', value: premium?.expired_total ?? '—' },
              { label: 'Nigdy nie mieli', value: premium?.never_total ?? '—' },
            ]}
          />
          <div className="row">
            <div className="muted">{premiumHint}</div>
          </div>

          <InputRow label="Status">
            <select value={pStatus} onChange={(e) => setPStatus(e.target.value as PremiumStatus)}>
              <option value="expired">Po terminie</option>
              <option value="expiring_7d">Kończą się ≤7 dni</option>
              <option value="active">Aktywne</option>
              <option value="never">Nigdy nie mieli Premium</option>
            </select>
          </InputRow>

          <InputRow label="Plany">
            <div className="inline">
              {(['BASIC', 'STANDARD', 'FAMILY'] as PremiumPlan[]).map((pl) => (
                <label key={pl}>
                  <input
                    type="checkbox"
                    checked={pPlans[pl]}
                    onChange={(e) => setPPlans((s) => ({ ...s, [pl]: e.target.checked }))}
                  />{' '}
                  {pl}
                </label>
              ))}
            </div>
            {pStatus === 'never' && (
              <div className="muted" style={{ marginTop: 4 }}>
                Dla użytkowników, którzy nigdy nie mieli Premium, filtr planu nie ma zastosowania.
              </div>
            )}
          </InputRow>

          <ChannelSelect value={pChannel} onChange={setPChannel} />

          <InputRow label="Tytuł">
            <input value={pTitle} onChange={(e) => setPTitle(e.target.value)} />
          </InputRow>

          {pChannel === 'inapp' && (
            <>
              <InputRow label="Treść krótka (push + lista)">
                <textarea rows={3} value={pMsg} onChange={(e) => setPMsg(e.target.value)} />
              </InputRow>
              <InputRow label="Treść szczegółowa (centrum powiadomień, opcjonalna)">
                <textarea rows={5} value={pLongMsg} onChange={(e) => setPLongMsg(e.target.value)} />
              </InputRow>
            </>
          )}

          {pChannel === 'email' && (
            <>
              <InputRow label="Szablon e-mail (wybierz)">
                <select
                  value={pTemplateKey}
                  onChange={async (e) => {
                    const key = e.target.value;
                    setPTemplateKey(key);

                    if (key) {
                      await loadMeta('p', key);
                      try {
                        setPPreview(await previewTemplate(key));
                        setPPreviewErr(null);
                      } catch (err: any) {
                        setPPreview(null);
                        setPPreviewErr(err?.message || 'Błąd podglądu');
                      }
                    } else {
                      // brak wybranego szablonu – czyścimy meta/podgląd
                      setPTplMeta(null);
                      setPPreview(null);
                      setPPreviewErr(null);
                    }
                  }}
                >
                  <option value="">— wybierz —</option>
                  {templates.map((t) => (
                    <option key={t.tkey} value={t.tkey}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </InputRow>

              <TemplateRow
                label="Bieżący szablon"
                templateKey={pTemplateKey}
                templateName={tplName(pTemplateKey)}
                canEdit={!!pTplMeta}
                editOpen={pEditOpen}
                onToggleEdit={() => setPEditOpen((v) => !v)}
              />

              {pTplMeta && pEditOpen && (
                <TemplateTextareaEditor
                  subject={pEditSubject}
                  html={pEditHtml}
                  onChangeSubject={setPEditSubject}
                  onChangeHtml={setPEditHtml}
                  onSave={async () => {
                    await updateEmailTemplate(pTemplateKey, { subject: pEditSubject, html: pEditHtml });
                    await loadMeta('p', pTemplateKey);
                    try {
                      setPPreview(await previewTemplate(pTemplateKey));
                      setPPreviewErr(null);
                    } catch (e: any) {
                      setPPreview(null);
                      setPPreviewErr(e?.message || 'Błąd podglądu');
                    }
                    setPEditOpen(false);
                  }}
                />
              )}

              {pPreviewErr && <div className="muted error">{pPreviewErr}</div>}
              <TemplatePreview preview={pPreview} />
            </>
          )}

          <ActionsRow onClick={sendPremium} loading={loading} label="Wyślij" primary />
        </AccordionItem>

        {/* POJEDYNCZY UŻYTKOWNIK */}
        <AccordionItem
          title="Pojedynczy użytkownik"
          open={openSingleUser}
          onOpenChange={(o) => {
            setOpenSingleUser(o);
            if (o && uTemplateKey) void loadMeta('u', uTemplateKey);
          }}
        >
          <InputRow label="User ID">
            <input
              type="number"
              value={uUserId}
              onChange={(e) => setUUserId(e.target.value)}
              placeholder="np. 61"
            />
          </InputRow>

          <ChannelSelect value={uChannel} onChange={setUChannel} />

          {uChannel === 'inapp' && (
            <>
              <InputRow label="Tytuł">
                <input value={uTitle} onChange={(e) => setUTitle(e.target.value)} />
              </InputRow>
              <InputRow label="Treść krótka (push + lista)">
                <textarea rows={3} value={uMsg} onChange={(e) => setUMsg(e.target.value)} />
              </InputRow>
              <InputRow label="Treść szczegółowa (centrum powiadomień, opcjonalna)">
                <textarea rows={5} value={uLongMsg} onChange={(e) => setULongMsg(e.target.value)} />
              </InputRow>
            </>
          )}

          {uChannel === 'email' && (
            <>
              <InputRow label="Szablon e-mail (wybierz)">
                <select
                  value={uTemplateKey}
                  onChange={async (e) => {
                    const key = e.target.value;
                    setUTemplateKey(key);
                    if (key) {
                      await loadMeta('u', key);
                    } else {
                      setUTplMeta(null);
                      setUPreview(null);
                      setUPreviewErr(null);
                      setUEmailSubject('');
                    }
                  }}
                >
                  <option value="">— wybierz —</option>
                  {templates.map((t) => (
                    <option key={t.tkey} value={t.tkey}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </InputRow>

              <InputRow label="Temat e-mail (opcjonalny, nadpisze temat z szablonu)">
                <input
                  value={uEmailSubject}
                  onChange={(e) => setUEmailSubject(e.target.value)}
                  placeholder={uTplMeta?.subject || 'Temat z szablonu'}
                  disabled={!uTemplateKey}
                />
              </InputRow>

              <TemplateRow
                label="Bieżący szablon"
                templateKey={uTemplateKey}
                templateName={tplName(uTemplateKey)}
                canEdit={!!uTplMeta}
                editOpen={uEditOpen}
                onToggleEdit={() => setUEditOpen((v) => !v)}
              />

              {uTplMeta && uEditOpen && (
                <TemplateTextareaEditor
                  subject={uEditSubject}
                  html={uEditHtml}
                  onChangeSubject={setUEditSubject}
                  onChangeHtml={setUEditHtml}
                  onSave={async () => {
                    await updateEmailTemplate(uTemplateKey, {
                      subject: uEditSubject,
                      html: uEditHtml,
                    });
                    await loadMeta('u', uTemplateKey);
                    try {
                      setUPreview(await previewTemplate(uTemplateKey));
                      setUPreviewErr(null);
                    } catch (e: any) {
                      setUPreview(null);
                      setUPreviewErr(e?.message || 'Błąd podglądu');
                    }
                    setUEditOpen(false);
                  }}
                />
              )}

              {uPreviewErr && <div className="muted error">{uPreviewErr}</div>}
              <TemplatePreview preview={uPreview} />
            </>
          )}

          <ActionsRow onClick={sendSingleUser} loading={loading} label="Wyślij do użytkownika" primary />
        </AccordionItem>
      </div>
    </div>
  );
}

/* ===================== Klocki UI ===================== */

function AccordionItem({
  title,
  children,
  open,
  onOpenChange,
  danger,
}: {
  title: string;
  children: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  danger?: boolean;
}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const controlled = typeof open === 'boolean';
  const isOpen = controlled ? open : internalOpen;
  const toggle = () => {
    const next = !isOpen;
    if (controlled) onOpenChange?.(next);
    else setInternalOpen(next);
  };
  return (
    <div className={`acc-item ${isOpen ? 'open' : ''}`}>
      <button className="acc-head" onClick={toggle}>
        <span className="acc-title">
          {title}
          {danger && (
            <span
              className="acc-danger"
              title="Uwaga: kanał e-mail w tej sekcji może ignorować zgody marketingowe."
              style={{ marginLeft: 8, color: '#b91c1c' }}
            >
              ⚠️
            </span>
          )}
        </span>
        <span className="chev">{isOpen ? '▾' : '▸'}</span>
      </button>
      <div className="acc-body">{children}</div>
    </div>
  );
}

function SectionStats({ rows }: { rows: Array<{ label: string; value: any }> }) {
  return (
    <div className="row">
      <div className="stat-cards">
        {rows.map((r) => (
          <Stat key={r.label} label={r.label} value={r.value} />
        ))}
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: any }) {
  return (
    <div className="stat">
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value ?? '—'}</div>
    </div>
  );
}

function InputRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="row">
      <label className="lbl">{label}</label>
      {children}
    </div>
  );
}

function ActionsRow({
  onClick,
  loading,
  label,
  primary,
}: {
  onClick: () => void;
  loading: boolean;
  label: string;
  primary?: boolean;
}) {
  return (
    <div className="row row-actions">
      <button className={`btn ${primary ? 'primary' : ''}`} onClick={onClick} disabled={loading}>
        {label}
      </button>
    </div>
  );
}

function ChannelSelect({ value, onChange }: { value: Channel; onChange: (c: Channel) => void }) {
  return (
    <div className="row">
      <label className="lbl">Kanał</label>
      <div className="inline" style={{ gap: 16 }}>
        <label>
          <input type="radio" checked={value === 'inapp'} onChange={() => onChange('inapp')} /> In-app
        </label>
        <label>
          <input type="radio" checked={value === 'email'} onChange={() => onChange('email')} /> E-mail
        </label>
      </div>
    </div>
  );
}

function TemplateRow({
  label,
  templateKey,
  templateName,
  canEdit,
  editOpen,
  onToggleEdit,
}: {
  label: string;
  templateKey: string;
  templateName: string;
  canEdit: boolean;
  editOpen: boolean;
  onToggleEdit: () => void;
}) {
  return (
    <div className="row">
      <label className="lbl">{label}</label>
      <div className="inline" style={{ gap: 8, alignItems: 'center' }}>
        <strong>{templateKey ? templateName : '— brak —'}</strong>
        {templateKey && canEdit && (
          <button
            className="btn small"
            onClick={onToggleEdit}
            title={editOpen ? 'Zablokuj edycję' : 'Odblokuj edycję'}
          >
            {editOpen ? '🔓 Odblokowane' : '🔒 Zablokowane'}
          </button>
        )}
      </div>
    </div>
  );
}

function buildEmailDoc(rawHtml: string, opts?: { stageWidth?: number; wrap?: boolean }) {
  const stageWidth = opts?.stageWidth ?? 680;
  const wrap = opts?.wrap ?? true;

  const html = String(rawHtml || '').replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '');

  const baseStyles = `
    html,body{margin:0;padding:0;background:#f5f7fb;}
    body{font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, Helvetica, sans-serif; line-height:1.45;}
    .email-sim-root{width:100%;}
    .email-sim-stage{max-width:${stageWidth}px;margin:24px auto;background:#fff;border-radius:10px;overflow:hidden;
      box-shadow:0 1px 4px rgba(0,0,0,.06);}
    table{border-collapse:collapse;mso-table-lspace:0pt;mso-table-rspace:0pt;}
    img{border:0;outline:0;text-decoration:none;display:block;max-width:100%;height:auto;}
    p{margin:0;}
    a{color:inherit;text-decoration:none;}
    @media (max-width: ${stageWidth + 40}px){ .email-sim-stage{border-radius:0;margin:0;} }
  `;

  const bodyContent = wrap
    ? `<div class="email-sim-root"><div class="email-sim-stage">${html}</div></div>`
    : html;

  return `<!doctype html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1"/>
  <meta name="color-scheme" content="light only">
  <meta name="supported-color-schemes" content="light">
  <base target="_blank"/>
  <style>${baseStyles}</style>
</head>
<body>${bodyContent}</body>
</html>`;
}

function EmailIframePreview({
  html,
  stageWidth = 680,
  wrap = true,
}: {
  html: string;
  stageWidth?: number;
  height?: number;
  wrap?: boolean;
}) {
  const doc = buildEmailDoc(html, { stageWidth, wrap });
  return (
    <iframe
      title="email-preview"
      style={{ width: '100%', minHeight: 500, border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff' }}
      srcDoc={doc}
      sandbox="allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation"
    />
  );
}

function TemplatePreview({ preview }: { preview: RenderedPreview }) {
  if (!preview) return null;
  return (
    <div className="tmpl-preview">
      <div className="tmpl-preview-head">
        <strong>Podgląd (zapisany na serwerze)</strong>
        <div className="muted">
          Temat: <em>{preview.subject}</em>
        </div>
      </div>
      <EmailIframePreview html={preview.html} />
    </div>
  );
}

/* ====== Prosty edytor textarea z lokalnym podglądem ====== */
function TemplateTextareaEditor({
  subject,
  html,
  onChangeSubject,
  onChangeHtml,
  onSave,
}: {
  subject: string;
  html: string;
  onChangeSubject: (v: string) => void;
  onChangeHtml: (v: string) => void;
  onSave: () => Promise<void> | void;
}) {
  return (
    <div className="tmpl-editor">
      <div className="row">
        <label className="lbl">Temat (subject)</label>
        <input value={subject} onChange={(e) => onChangeSubject(e.target.value)} />
      </div>
      <div className="row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <div className="muted" style={{ marginBottom: 6 }}>
            HTML szablonu
          </div>
          <textarea
            rows={18}
            value={html}
            onChange={(e) => onChangeHtml(e.target.value)}
            style={{ width: '100%', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
          />
        </div>
        <div>
          <div className="muted" style={{ marginBottom: 6 }}>
            Podgląd (lokalny)
          </div>
          <EmailIframePreview
            html={html || '<div style="padding:24px;color:#888">Brak treści…</div>'}
            stageWidth={680}
            wrap
          />
        </div>
      </div>
      <div className="row row-actions">
        <button className="btn primary" onClick={onSave}>
          Zapisz szablon
        </button>
      </div>
      <div className="muted" style={{ marginTop: 6 }}>
        Po zapisie poniżej odświeży się „Podgląd (zapisany na serwerze)”.
      </div>
    </div>
  );
}

/* ====== Helpers tekstowe ====== */
function defaultLegalTitle(change: LegalChange) {
  switch (change) {
    case 'TERMS':
      return 'Zmieniliśmy Regulamin';
    case 'PRIVACY':
      return 'Zmieniliśmy Politykę prywatności';
    case 'BOTH':
      return 'Aktualizacja Regulaminu i Polityki prywatności';
  }
}
function defaultLegalMessage(change: LegalChange) {
  switch (change) {
    case 'TERMS':
      return 'Opublikowaliśmy nową wersję Regulaminu. Przy następnym logowaniu poprosimy o akceptację.';
    case 'PRIVACY':
      return 'Opublikowaliśmy nową wersję Polityki prywatności. Przy następnym logowaniu poprosimy o akceptację.';
    case 'BOTH':
      return 'Opublikowaliśmy nowe wersje Regulaminu i Polityki prywatności. Przy następnym logowaniu poprosimy o akceptację.';
  }
}
